import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Grid,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ToggleOn,
  ToggleOff,
  Person,
  Close,
  Login as LoginIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { User, AccountType, AccessTypeAnalytics } from '../../types/auth';
import { userApi } from '../../services/api/userApi';
import { apiClient } from '../../services/api/authApi';
import { useAuthStore } from '../../store/authStore';
import {
  validateRbacFields,
  validateRequiredUserFields,
  makeHandleInputChange,
  makeUserFormChangeHandlers,
  applyUserStatusToggle,
} from '../users/userFormShared';
import UserFormDialog, { renderElevatedRoleMenuItems } from '../users/UserFormDialog';
import { useUserFormState } from '../users/useUserFormState';

interface UserManagerProps {
  companyId: number;
  companyName: string;
  open: boolean;
  onClose: () => void;
  initialOpenAddDialog?: boolean;
  initialAccountType?: AccountType;
}

const UserManager: React.FC<UserManagerProps> = ({
  companyId,
  companyName,
  open,
  onClose,
  initialOpenAddDialog = false,
  initialAccountType,
}) => {
  const { user: currentUser } = useAuthStore();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const {
    formData,
    setFormData,
    validationErrors,
    setValidationErrors,
    subCompanies,
    branches,
    subCustomers,
    selectedSubCompanyId,
    setSelectedSubCompanyId,
    selectedBranchId,
    setSelectedBranchId,
    selectedSubCustomerId,
    setSelectedSubCustomerId,
    loadingOrgData,
    loadOrgData,
  } = useUserFormState(editingUser, companyId);

  useEffect(() => {
    if (open && companyId) {
      loadUsers();
      loadOrgData(companyId);

      if (initialOpenAddDialog) {
        handleCreateClick(initialAccountType);
      }
    }
  }, [open, companyId, initialOpenAddDialog]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await userApi.getByCompany(companyId);
      setUsers(data);
    } catch (error: any) {
      console.error('Failed to load users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = (defaultAccountType?: AccountType) => {
    setEditingUser(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      // password: '',
      accountType: defaultAccountType || AccountType.User,
      companyId,
      branch: '',
      subCompany: '',
      subCustomerId: undefined,
      mail: '',
      accessTypeAnalytics: AccessTypeAnalytics.Passive,
      dataStorageKey: '',
      passKey: '',
    });
    setValidationErrors({});
    setSelectedSubCompanyId(null);
    setSelectedBranchId(null);
    setSelectedSubCustomerId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (user: User) => {
    setEditingUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      // password: '', // Don't pre-fill password for security
      accountType: user.accountType,
      companyId: user.companyId,
      branch: user.branch || '',
      subCompany: user.subCompany || '',
      subCustomerId: user.subCustomerId || undefined,
      mail: user.mail || '',
      accessTypeAnalytics: user.accessTypeAnalytics || AccessTypeAnalytics.Passive,
      dataStorageKey: user.dataStorageKey || '',
      passKey: user.passKey || '',
    });

    // Clear selections; they will be resolved after org data loads
    setSelectedSubCompanyId(null);
    setSelectedBranchId(null);
    setSelectedSubCustomerId(user.subCustomerId || null);

    setValidationErrors({});
    setOpenDialog(true);
  };

  const handleInputChange = makeHandleInputChange(formData, setFormData, setValidationErrors, editingUser);

  const handleSubmit = async () => {
    try {
      // Final validation
      const errors: Record<string, string> = {};
      validateRequiredUserFields(formData, errors);

      // Validate RBAC role requirements
      validateRbacFields(formData, errors, 'Licensee Admin');

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return;
      }

      if (editingUser) {
        const updateData = { ...formData };
        // Don't send empty password
        // if (!updateData.password) {
        //   delete (updateData as any).password;
        // }

        const updatedUser = await userApi.update(editingUser.id, updateData);
        setUsers(users.map(u => u.id === editingUser.id ? updatedUser : u));
        setSuccess('User updated successfully');
      } else {
        const response = await userApi.create(formData);
        // Handle response which might be { user, emailSent } or just the user
        const newUser = response.user || response;

        // Ensure the new user has an id
        if (newUser.id) {
          setUsers([...users, newUser]);
        } else {
          console.error('Created user missing id:', newUser);
          // Reload the list to get the proper data
          await loadUsers();
        }

        const message = response.emailSent
          ? 'User created successfully. Welcome email sent.'
          : 'User created successfully';
        setSuccess(message);
      }
      setOpenDialog(false);
    } catch (error: any) {
      setError(error.response?.data?.error || error.response?.data?.message || 'Failed to save user');
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      const result = await applyUserStatusToggle(user, users);
      setUsers(result.users);
      setSuccess(result.successMessage);
    } catch (error: any) {
      console.error('Failed to update user status:', error);
      setError('Failed to update user status');
    }
  };

  const handleDelete = async (user: User) => {
    if (globalThis.confirm(`Are you sure you want to delete ${user.firstName} ${user.lastName}?`)) {
      try {
        await userApi.delete(user.id);
        setUsers(users.filter(u => u.id !== user.id));
        setSuccess('User deleted successfully');
      } catch (error: any) {
        console.error('Failed to delete user:', error);
        setError('Failed to delete user');
      }
    }
  };

  const handleLoginAs = async (targetUser: User) => {
    if (globalThis.confirm(`Are you sure you want to login as ${targetUser.firstName} ${targetUser.lastName}?`)) {
      try {
        const response = await apiClient.post(`/auth/impersonate/${targetUser.id}`);
        if (response.data.success) {
          const authStore = useAuthStore.getState();
          authStore.setAuth(response.data.data.user, response.data.data.token);
          onClose();
          navigate('/settings', { replace: true });
        }
      } catch (error: any) {
        setError(error.response?.data?.error || 'Failed to impersonate user');
      }
    }
  };

  const getAccountTypeLabel = (type: AccountType): string => {
    switch (type) {
      case AccountType.User:
        return 'User';
      case AccountType.CarbonCareAdmin:
        return 'Admin';
      case AccountType.LicenseeAdmin:
        return 'Licensee Admin';
      case AccountType.MasterAdmin:
        return 'Master Admin';
      case AccountType.BranchAdmin:
        return 'Branch Admin';
      case AccountType.SubCompanyAdmin:
        return 'Sub Company Admin';
      case AccountType.SubCustomerAdmin:
        return 'SubCustomer Admin';
      default:
        return 'Unknown';
    }
  };

  const getAccountTypeColor = (type: AccountType) => {
    switch (type) {
      case AccountType.User:
        return 'default';
      case AccountType.CarbonCareAdmin:
        return 'primary';
      case AccountType.LicenseeAdmin:
        return 'secondary';
      case AccountType.MasterAdmin:
        return 'error';
      case AccountType.BranchAdmin:
        return 'warning';
      case AccountType.SubCompanyAdmin:
        return 'success';
      case AccountType.SubCustomerAdmin:
        return 'info';
      default:
        return 'default';
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const showActionsColumn = currentUser?.accountType !== AccountType.CarbonCareAdmin;
  const totalColumns = 6 + (showActionsColumn ? 1 : 0);
  const pagedUsers = users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person />
              <Typography variant="h6">
                Users for {companyName}
              </Typography>
            </Box>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              Manage user accounts for this company
            </Typography>
            {currentUser?.accountType !== AccountType.CarbonCareAdmin && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleCreateClick()}
                size="small"
              >
                Add User
              </Button>
            )}
          </Box>

          <TableContainer component={Paper}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      {showActionsColumn && <TableCell align="center">Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={totalColumns} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No users found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      pagedUsers.map((u: any) => {
                        const isActive = u.isActive ?? u.is_active ?? false;
                        const canLoginAs =
                          currentUser?.accountType === AccountType.MasterAdmin &&
                          u.id !== currentUser?.id &&
                          u.accountType !== AccountType.MasterAdmin;
                        return (
                          <TableRow key={u.id} hover>
                            <TableCell>{`${u.firstName} ${u.lastName}`}</TableCell>
                            <TableCell>{u.email}</TableCell>
                            <TableCell>
                              <Chip
                                label={getAccountTypeLabel(u.accountType)}
                                color={getAccountTypeColor(u.accountType)}
                                size="small"
                              />
                            </TableCell>

                            <TableCell>
                              <Chip
                                label={isActive ? 'Active' : 'Inactive'}
                                color={isActive ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            {showActionsColumn && (
                              <TableCell align="center">
                                <Tooltip title="Edit">
                                  <IconButton size="small" onClick={() => handleEditClick(u)}>
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {canLoginAs && (
                                  <Tooltip title="Login as this user">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleLoginAs(u)}
                                      sx={{ color: 'primary.main' }}
                                    >
                                      <LoginIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title={isActive ? 'Deactivate' : 'Activate'}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleToggleStatus({ ...u, isActive })}
                                    sx={{ color: isActive ? 'success.main' : 'error.main' }}
                                  >
                                    {isActive ? <ToggleOn fontSize="small" /> : <ToggleOff fontSize="small" />}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDelete(u)}
                                      disabled={u.id === currentUser?.id}
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={users.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </TableContainer>
        </DialogContent>
      </Dialog>

      {/* Create/Edit User Dialog */}
      <UserFormDialog
        open={openDialog}
        editingUser={editingUser}
        formData={formData}
        validationErrors={validationErrors}
        subCompanies={subCompanies}
        branches={branches}
        subCustomers={subCustomers}
        selectedSubCompanyId={selectedSubCompanyId}
        selectedBranchId={selectedBranchId}
        selectedSubCustomerId={selectedSubCustomerId}
        loadingOrgData={loadingOrgData}
        onCancel={() => setOpenDialog(false)}
        onSubmit={handleSubmit}
        onInputChange={handleInputChange}
        {...makeUserFormChangeHandlers(
          setSelectedSubCompanyId,
          setSelectedBranchId,
          setSelectedSubCustomerId,
          handleInputChange,
        )}
        subCompanyAdminHintText="Required for Licensee Admin role"
        companySectionSlot={
          <>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 2 }}>
              Role
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={formData.accountType}
                    onChange={(e) => handleInputChange('accountType', e.target.value)}
                    label="Role"
                    error={!!validationErrors.accountType}
                  >
                    {/* Sub Customer Admin / Branch Admin / Sub Company Admin temporarily
                        hidden from the UI — uncomment this block to re-enable them.
                    {currentUser?.accountType! >= AccountType.SubCompanyAdmin && [
                      <MenuItem key="sub-customer-admin" value={AccountType.SubCustomerAdmin}>Sub Customer Admin</MenuItem>,
                      <MenuItem key="branch-admin" value={AccountType.BranchAdmin}>Branch Admin</MenuItem>,
                      <MenuItem key="company-admin" value={AccountType.SubCompanyAdmin}>Sub Company Admin</MenuItem>,
                    ]}
                    */}
                    {renderElevatedRoleMenuItems(currentUser?.accountType)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </>
        }
      />

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UserManager;